import "tom-select/dist/css/tom-select.default.css";

import dayjs from "dayjs";
import { html } from "lit-html";
import TomSelect from "tom-select";

import loaderCircleSvg from "~/img/svg/loader-circle.svg";
import trashSvg from "~/img/svg/trash.svg";
import xSvg from "~/img/svg/x.svg";
import { cloneUserReport, createOrUpdateUserReport, deleteUserReport } from "~/js/api.js";
import { base64ToSvg, cn } from "~/js/helpers.js";
import { Popover } from "~/js/utils/popover.js";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

const STATES = {
	FULFILLED: 0,
	PENDING: 1,
};

let filterRowCounter = 0;

class FiltersBuilder {
	#container;
	#addButton;
	#emptyState;
	#definitions;
	#definitionsByField;
	#operatorChoices;
	#rows = new Map();
	#tomSelectInstances = new Map();
	#getCommonTomSelectConfig;

	constructor(container, addButton, definitionsJson, getCommonTomSelectConfig) {
		this.#container = container;
		this.#addButton = addButton;
		this.#emptyState = container.querySelector("#filters-empty-state");
		this.#getCommonTomSelectConfig = getCommonTomSelectConfig;

		const parsed = JSON.parse(definitionsJson);
		this.#definitions = parsed.filters;
		this.#definitionsByField = new Map(this.#definitions.map((d) => [d.field, d]));
		this.#operatorChoices = parsed.operator_choices;

		// Pre-populate rows for filters that have values
		for (const defn of this.#definitions) {
			if (defn.value.length > 0) {
				this.#addRow(defn.field, defn.operator, defn.value, defn.selected_items);
			}
		}

		this.#addButton.addEventListener("click", () => this.#addRow());
	}

	#addRow(fieldName = null, operatorValue = "is", values = [], selectedItems = []) {
		const rowId = `filter-row-${++filterRowCounter}`;
		const row = document.createElement("div");
		row.id = rowId;
		row.className =
			"filter-row p-3 border dark:border-0 border-b-slate-900/10 dark:bg-white/5 rounded-lg [&>*]:empty:hidden grid grid-cols-[1fr_1fr_20px] [&>select:nth-child(2)]:w-full! sm:[&>select:nth-child(2)]:w-24! [&>button]:col-start-3 [&>button]:self-center sm:[&>button]:self-auto sm:[&>button]:min-h-[42px] [&>.ts-wrapper]:col-span-2 [&>.ts-wrapper]:col-start-1 [&>.ts-wrapper]:row-start-2 sm:flex items-start gap-2 justify-between";

		const selectBaseClass =
			"shrink-0 p-2.5 text-sm text-slate-900 dark:bg-slate-900 rounded-lg border border-slate-900/10 dark:text-white dark:border-white/10 dark:focus:border-white focus:outline-hidden focus:ring-0 focus:border-slate-900";

		// Field select
		const fieldSelect = document.createElement("select");
		fieldSelect.className = `filter-field w-40 ${selectBaseClass}`;
		this.#populateFieldSelect(fieldSelect, fieldName);
		row.appendChild(fieldSelect);

		// Operator select
		const operatorSelect = document.createElement("select");
		operatorSelect.className = `filter-operator w-24 ${selectBaseClass}`;
		for (const opt of this.#operatorChoices) {
			const option = document.createElement("option");
			option.value = opt.value;
			option.textContent = opt.text;
			if (opt.value === operatorValue) option.selected = true;
			operatorSelect.appendChild(option);
		}
		row.appendChild(operatorSelect);

		// Value select (will be enhanced with TomSelect)
		const valueSelect = document.createElement("select");
		valueSelect.className = "filter-value flex-1";
		valueSelect.multiple = true;
		row.appendChild(valueSelect);

		// Remove button
		const removeBtn = document.createElement("button");
		removeBtn.type = "button";
		removeBtn.className =
			"filter-remove text-slate-400 hover:text-black font-bold transition-colors dark:hover:text-white";
		const removeImg = document.createElement("img");
		removeImg.src = trashSvg;
		removeImg.alt = gettext("Rimuovi filtro");
		removeImg.className = "size-5 dark:invert opacity-50 hover:opacity-100 transition-opacity";
		removeBtn.replaceChildren(removeImg);
		removeBtn.addEventListener("click", () => this.#removeRow(rowId));
		row.appendChild(removeBtn);

		this.#container.appendChild(row);

		this.#rows.set(rowId, { fieldName, row, fieldSelect, operatorSelect });

		// Focus and open the field select for new empty rows
		if (!fieldName) {
			fieldSelect.focus();
			try {
				fieldSelect.showPicker();
			} catch {}
		}

		// Initialize the value TomSelect if a field is pre-selected
		if (fieldName) {
			const defn = this.#definitionsByField.get(fieldName);
			if (defn) {
				this.#updateOperatorVisibility(operatorSelect, defn.has_operator);
				this.#initValueTomSelect(rowId, valueSelect, defn, values, selectedItems);
			}
		} else {
			operatorSelect.classList.add("hidden");
			valueSelect.classList.add("hidden");
		}

		// Handle field change
		fieldSelect.addEventListener("change", () => {
			const newField = fieldSelect.value;
			const defn = this.#definitionsByField.get(newField);

			this.#rows.get(rowId).fieldName = newField;

			// Destroy old TomSelect
			const oldTs = this.#tomSelectInstances.get(rowId);
			if (oldTs) {
				oldTs.destroy();
				this.#tomSelectInstances.delete(rowId);
			}

			// Reset the value select element
			const newValueSelect = document.createElement("select");
			newValueSelect.className = "filter-value flex-1";
			newValueSelect.multiple = true;
			row.replaceChild(newValueSelect, row.querySelector(".filter-value"));

			if (defn) {
				this.#updateOperatorVisibility(operatorSelect, defn.has_operator);
				operatorSelect.value = "is";
				this.#initValueTomSelect(rowId, newValueSelect, defn, [], []);
			} else {
				operatorSelect.classList.add("hidden");
				newValueSelect.classList.add("hidden");
			}

			this.#refreshFieldSelects();
			this.#updateAddButton();
		});

		this.#refreshFieldSelects();
		this.#updateEmptyState();
		this.#updateAddButton();

		return rowId;
	}

	#removeRow(rowId) {
		const rowData = this.#rows.get(rowId);
		if (!rowData) return;

		const ts = this.#tomSelectInstances.get(rowId);
		if (ts) {
			ts.destroy();
			this.#tomSelectInstances.delete(rowId);
		}

		this.#rows.delete(rowId);
		rowData.row.remove();
		this.#refreshFieldSelects();
		this.#updateEmptyState();
		this.#updateAddButton();
	}

	#populateFieldSelect(select, selectedField) {
		const placeholder = document.createElement("option");
		placeholder.value = "";
		placeholder.textContent = gettext("Seleziona...");
		if (!selectedField) placeholder.selected = true;
		select.appendChild(placeholder);

		const usedFields = this.#getUsedFields();
		for (const defn of this.#definitions) {
			if (usedFields.has(defn.field) && defn.field !== selectedField) continue;
			const option = document.createElement("option");
			option.value = defn.field;
			option.textContent = defn.label;
			if (defn.field === selectedField) option.selected = true;
			select.appendChild(option);
		}
	}

	#refreshFieldSelects() {
		const usedFields = this.#getUsedFields();

		for (const [, rowData] of this.#rows) {
			const { fieldSelect } = rowData;
			const currentValue = fieldSelect.value;

			for (const option of Array.from(fieldSelect.options)) {
				if (!option.value) continue;
				const isUsedByOther = usedFields.has(option.value) && option.value !== currentValue;
				option.hidden = isUsedByOther;
				option.disabled = isUsedByOther;
			}

			// Add missing options that are now available
			const existingValues = new Set([...fieldSelect.options].map((o) => o.value));
			for (const defn of this.#definitions) {
				if (existingValues.has(defn.field)) continue;
				if (usedFields.has(defn.field) && defn.field !== currentValue) continue;
				const option = document.createElement("option");
				option.value = defn.field;
				option.textContent = defn.label;
				fieldSelect.appendChild(option);
			}
		}
	}

	#getUsedFields() {
		const used = new Set();
		for (const [, rowData] of this.#rows) {
			if (rowData.fieldName) used.add(rowData.fieldName);
		}
		return used;
	}

	#updateEmptyState() {
		if (this.#emptyState) {
			this.#emptyState.hidden = this.#rows.size > 0;
		}
	}

	#updateAddButton() {
		const hasIncompleteRow = [...this.#rows.values()].some((r) => !r.fieldName);
		const allFieldsUsed = this.#getUsedFields().size >= this.#definitions.length;
		this.#addButton.disabled = hasIncompleteRow || allFieldsUsed;
	}

	#updateOperatorVisibility(operatorSelect, hasOperator) {
		operatorSelect.classList.toggle("hidden", !hasOperator);
	}

	#initValueTomSelect(rowId, selectEl, defn, values, selectedItems) {
		selectEl.classList.remove("hidden");

		if (defn.is_autocomplete) {
			this.#initAutocompleteTomSelect(rowId, selectEl, values, selectedItems);
		} else {
			this.#initStaticTomSelect(rowId, selectEl, defn, values);
		}
	}

	#createTomSelect(rowId, selectEl, config) {
		const ts = new TomSelect(selectEl, {
			...this.#getCommonTomSelectConfig(),
			...config,
			onItemAdd: function () {
				this.setTextboxValue("");
				this.refreshOptions();
			},
		});
		this.#tomSelectInstances.set(rowId, ts);
		return ts;
	}

	#initStaticTomSelect(rowId, selectEl, defn, values) {
		for (const opt of defn.options) {
			const option = document.createElement("option");
			option.value = opt.value;
			option.textContent = opt.text;
			if (values.includes(opt.value)) option.selected = true;
			selectEl.appendChild(option);
		}

		this.#createTomSelect(rowId, selectEl, {
			allowEmptyOption: true,
			maxItems: null,
			maxOptions: 50,
			searchField: ["text", "groupName"],
			onInitialize: function () {
				if (Object.keys(this.optgroups).length > 0) {
					for (const key of Object.keys(this.options)) {
						this.updateOption(this.options[key].value, {
							...this.options[key],
							groupName: this.optgroups[this.options[key].optgroup].label,
						});
					}
				}
			},
		});
	}

	#initAutocompleteTomSelect(rowId, selectEl, values, selectedItems) {
		for (const item of selectedItems) {
			const option = document.createElement("option");
			option.value = item.id;
			option.textContent = item.text;
			if (item.favicon) option.dataset.favicon = item.favicon;
			option.selected = true;
			selectEl.appendChild(option);
		}

		this.#createTomSelect(rowId, selectEl, {
			valueField: "id",
			labelField: "text",
			searchField: ["text"],
			create: false,
			maxOptions: 5,
			shouldLoad: (query) => query.length > 1,
			load: (query, callback) => {
				const url = `/api/website/autocomplete?${new URLSearchParams({ q: query })}`;
				fetch(url)
					.then((response) => response.json())
					.then((data) => {
						const websites = data.websites.map((website) => ({
							id: website.id,
							text: website.domain,
							favicon: website.favicon_url,
						}));
						callback(websites);
					})
					.catch(() => callback());
			},
		});
	}

	serialize() {
		const data = {};

		for (const [rowId, rowData] of this.#rows) {
			if (!rowData.fieldName) continue;

			const ts = this.#tomSelectInstances.get(rowId);
			data[rowData.fieldName] = ts ? [...ts.items] : [];

			const defn = this.#definitionsByField.get(rowData.fieldName);
			if (defn?.has_operator) {
				data[`${rowData.fieldName}_operator`] = rowData.operatorSelect.value || "is";
			}
		}

		return data;
	}
}

const dialogInstances = new Set();
let scrolloptions = {};
const visibleDialogs = new Set();

export class Dialog {
	#abort = new AbortController();
	#callbacks;
	#canClearTimeouts = true;
	#closeButtonIcon = base64ToSvg(xSvg);
	#closeTimeout;
	#constructableSheets;
	#defaultClasses =
		"top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-4 xl:px-8 overflow-hidden overflow-y-auto w-11/12 max-w-[600px] transition-all backdrop:backdrop-blur-sm backdrop:bg-slate-900/30 rounded-sm bg-white shadow-xl shadow-slate-700/50 dark:bg-slate-900 dark:border dark:border-slate-800 dark:backdrop:bg-slate-900/80";
	#events;
	#openTimeout;
	#reject;
	#resolve;
	#sourceTarget;
	#state = STATES.PENDING;
	#styleSheets = new Map();

	constructor({
		classes = "",
		close,
		closeButton = true,
		closeOnBackdrop = true,
		constructableSheets,
		content,
		id,
		open,
		styleSheets,
	} = {}) {
		const instance = [...dialogInstances].find((dialog) => dialog.element.id === id);
		if (instance) {
			instance.content(content);
			return;
		}

		this.#callbacks = { close, open };
		this.closeButton = closeButton;
		this.closeOnBackdrop = closeOnBackdrop;
		this.element = document.createElement("dialog");

		this.#constructableSheets = []
			.concat(constructableSheets)
			.filter((sheet) => sheet instanceof CSSStyleSheet);
		this.#findStyleSheets([].concat(styleSheets).filter((sheet) => typeof sheet === "string"));

		this.#events = {
			click: this.#onClick.bind(this),
			keydown: this.#onKeydown.bind(this),
			pointerdown: this.#onPointerdown.bind(this),
		};

		// Merge classes with existing classes
		this.element.className = cn(this.#defaultClasses, classes);

		if (id) {
			this.element.id = id;
		}

		this.content(content);
		dialogInstances.add(this);
	}

	get #hasContent() {
		return this.element.childNodes.length > 0;
	}

	get #isLastVisible() {
		return [...visibleDialogs].pop() === this;
	}

	#attachConstructableSheets() {
		for (const sheet of this.#constructableSheets) {
			document.adoptedStyleSheets.push(sheet);
		}
	}

	#attachStyleSheets() {
		return Promise.allSettled(
			[...this.#styleSheets].map(
				([url, sheet]) =>
					new Promise((resolve, reject) => {
						if (sheet) {
							return resolve();
						}

						const resource = document.createElement("link");
						resource.href = url;
						resource.rel = "stylesheet";
						resource.addEventListener("load", resolve);
						resource.addEventListener("error", reject);
						document.head.appendChild(resource);
						this.#styleSheets.set(url, resource);
					}),
			),
		);
	}

	#clearTimeouts() {
		if (this.#canClearTimeouts) {
			clearTimeout(this.#closeTimeout);
			clearTimeout(this.#openTimeout);
		}

		this.#canClearTimeouts = true;
	}

	#detachConstructableSheets() {
		document.adoptedStyleSheets = document.adoptedStyleSheets.filter(
			(sheet) => !this.#constructableSheets.includes(sheet),
		);
	}

	#detachStyleSheets() {
		for (const [url, sheet] of this.#styleSheets) {
			if (sheet) {
				sheet.remove();
				this.#styleSheets.set(url, null);
			}
		}
	}

	#findStyleSheets(styleSheets) {
		for (const sheet of styleSheets) {
			const url = typeof sheet === "string" ? new URL(sheet, document.baseURI).href : sheet.href;
			if (!this.#styleSheets.has(url)) {
				this.#styleSheets.set(url, null);
				sheet.remove?.();
			}
		}
	}

	#lockScroll(force) {
		if (visibleDialogs.size > 0) {
			return;
		}

		if (force) {
			if (isIOS) {
				scrolloptions = {
					left: window.scrollX,
					top: window.scrollY,
					behavior: "instant",
				};
				document.documentElement.style.cssText = `left: -${scrolloptions.left}px; position: fixed; top: -${scrolloptions.top}px;`;
			} else {
				document.documentElement.style.cssText = `overflow: hidden; padding-right: ${
					window.innerWidth - document.documentElement.getBoundingClientRect().width
				}px`;
			}
		} else {
			document.documentElement.style.cssText = null;
			if (isIOS) {
				document.documentElement.scrollTo(scrolloptions);
			}
		}
	}

	#onClick(e) {
		if (this.#isLastVisible) {
			if (
				this.#sourceTarget === document.documentElement &&
				e.target === document.documentElement &&
				this.closeOnBackdrop
			) {
				if (this.#wasClickedInBackdrop(e)) {
					this.close();
				}
			} else {
				const target = e.target.closest("[data-dialog-action]");

				if (target) {
					const actions = e.target.dataset.dialogAction.toLowerCase().split(/\s+/);

					(async () => {
						for (const action of actions) {
							await this[action]?.();
						}
					})();
				}
			}
			this.#sourceTarget = null;
		}
	}

	#onKeydown(e) {
		if (e.key === "Escape" && this.#isLastVisible) {
			e.preventDefault();
			this.close();
		}
	}

	#onPointerdown(e) {
		if (this.#isLastVisible) {
			if (e.target === document.documentElement) {
				// prevent user-select in firefox
				e.preventDefault();
			}

			this.#sourceTarget = e.target;
		}
	}

	#resolveAnimations() {
		return Promise.allSettled(this.element.getAnimations().map((animation) => animation.finished));
	}

	async #resolveCallback(callback, value) {
		if (this.#state === STATES.PENDING) {
			await callback?.(value);
		}
		this.#state = STATES.FULFILLED;
		return Promise.resolve();
	}

	#wasClickedInBackdrop(e) {
		const { left, right, top, bottom } = this.element.getBoundingClientRect();
		return left > e.clientX || right < e.clientX || top > e.clientY || bottom < e.clientY;
	}

	abort() {
		this.#abort.abort();
		this.#abort = new AbortController();

		if (!this.#hasContent) {
			visibleDialogs.delete(this);
		}
		return this;
	}

	async close(delay) {
		this.#clearTimeouts();

		if (typeof delay === "number" && delay > 0) {
			await new Promise((resolve) => {
				this.#closeTimeout = setTimeout(() => resolve(), delay);
			});
		}

		if (this.element.open) {
			document.removeEventListener("click", this.#events.click);
			document.removeEventListener("keydown", this.#events.keydown);
			document.removeEventListener("pointerdown", this.#events.pointerdown);

			this.element.dataset.dialogState = "close";
			await this.#resolveAnimations();

			dialogInstances.delete(this);
			visibleDialogs.delete(this);

			this.element.close();
			this.element.remove();
			this.#lockScroll(false);
			this.#detachConstructableSheets();
			this.#detachStyleSheets();
			this.#callbacks.close?.(this);
			await this.reject();
			this.#state = STATES.PENDING;
		}

		return Promise.resolve();
	}

	async content(value) {
		if (value != null) {
			this.#clearTimeouts();
			this.abort();

			if (typeof value === "function") {
				return value(this, this.#abort.signal);
			}

			const element = document.createElement("div");

			if (value instanceof HTMLElement) {
				element.append(
					value instanceof HTMLTemplateElement ? value.content.cloneNode(true) : value,
				);
			} else {
				element.innerHTML = value;
			}

			if (this.closeButton) {
				const closeButton = document.createElement("button");
				closeButton.append(this.#closeButtonIcon);
				closeButton.className =
					"absolute flex items-center justify-center top-0 right-0 m-2 rounded-lg p-1 h-8 w-8 opacity-50 hover:opacity-100 transition-opacity text-slate-400 hover:text-black dark:hover:text-white focus:outline-hidden focus:ring-2 focus:ring-slate-400 focus:opacity-100 dark:focus:ring-white dark:focus:[&>svg]:text-white";
				closeButton.addEventListener("click", () => this.close());
				element.prepend(closeButton);
			}

			this.#findStyleSheets([...element.querySelectorAll('link[rel="stylesheet"]')]);
			await this.#attachStyleSheets();
			this.element.replaceChildren(...element.childNodes);
		}

		this.#canClearTimeouts = false;

		return Promise.resolve(this.element);
	}

	async open(delay) {
		this.#clearTimeouts();

		if (typeof delay === "number" && delay > 0) {
			await new Promise((resolve) => {
				this.#openTimeout = setTimeout(() => resolve(), delay);
			});
		}

		if (!this.element.open) {
			this.#attachConstructableSheets();
			await this.#attachStyleSheets();

			document.addEventListener("click", this.#events.click);
			document.addEventListener("keydown", this.#events.keydown);
			document.addEventListener("pointerdown", this.#events.pointerdown);

			if (!document.contains(this.element)) {
				document.body.appendChild(this.element);
			}

			this.#lockScroll(true);

			// Show spinner while loading
			const html_ = html`
        <div
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          <div class="flex space-x-2 justify-center items-center">
            <span class="sr-only">${gettext("Caricamento")}...</span>
            <div
              class="h-3 w-3 dark:bg-white/25 bg-black/25 rounded-full animate-bounce [animation-delay:-0.3s]"
            ></div>
            <div
              class="h-3 w-3 dark:bg-white/25 bg-black/25 rounded-full animate-bounce [animation-delay:-0.15s]"
            ></div>
            <div
              class="h-3 w-3 dark:bg-white/25 bg-black/25 rounded-full animate-bounce"
            ></div>
          </div>
        </div>
      `;
			this.element.innerHTML = html_.strings;

			// Finally, show the dialog
			this.element.showModal();
			this.element.dataset.dialogState = "open";

			dialogInstances.add(this);
			visibleDialogs.add(this);

			await this.#resolveAnimations();
			this.#callbacks.open?.(this);
		}

		return Promise.resolve();
	}

	success(callback) {
		if (typeof callback === "function") {
			this.#resolve = callback;
		}
		return this;
	}

	catch(callback) {
		if (typeof callback === "function") {
			this.#reject = callback;
		}
		return this;
	}

	reject(reason) {
		return this.#resolveCallback(this.#reject, reason);
	}

	resolve(value) {
		return this.#resolveCallback(this.#resolve, value);
	}
}

export class UserReportDialog extends Dialog {
	#form;
	#dateRangeStartInputEl;
	#dateRangeEndInputEl;
	#filtersBuilder;
	// Instaces
	#tooltipsInstances = [];
	// Dayjs instance
	#yesterday = dayjs().subtract(1, "day");
	constructor({ reportId = null, ...options }) {
		super(options);
		this.reportId = reportId;
	}

	async content(value) {
		await super.content(value);
		this.#init();
	}

	#init() {
		this.#form = this.element.querySelector("form");
		if (!this.#form) return;

		// Only one time dimension can be selected
		const timeDimensionInputs = this.element.querySelectorAll('input[name="time_dimension"]');
		for (const input of timeDimensionInputs) {
			input.addEventListener("change", (e) => {
				for (const input of timeDimensionInputs) {
					if (input !== e.target) {
						input.checked = false;
					}
				}
			});
		}

		this.#dateRangeStartInputEl = this.element.querySelector("input[name='date_range_start']");
		this.#dateRangeEndInputEl = this.element.querySelector("input[name='date_range_end']");

		// Show date range inputs if custom is selected
		const dateRangeTypeInput = this.element.querySelector("select[name='date_range_type']");
		// Show/hide date range inputs on load
		if (dateRangeTypeInput.value === "custom") {
			this.#showDateRangeInputs();
		} else {
			this.#hideDateRangeInputs();
		}
		// Show/hide date range inputs on change
		dateRangeTypeInput.addEventListener("change", (e) => {
			if (e.target.value === "custom") {
				this.#showDateRangeInputs();
			} else {
				this.#hideDateRangeInputs();
			}
		});

		// Initialize filters builder (dynamic filter rows)
		const filtersContainer = this.element.querySelector("#filters-builder");
		const addFilterBtn = this.element.querySelector("#add-filter-btn");
		const filterDefsEl = this.element.querySelector("#filter-definitions");
		if (filtersContainer && addFilterBtn && filterDefsEl) {
			this.#filtersBuilder = new FiltersBuilder(
				filtersContainer,
				addFilterBtn,
				filterDefsEl.textContent,
				() => this.#getCommonTomSelectConfig(),
			);
		}

		const submitButton = this.element.querySelector("button[type=submit]");
		if (submitButton) {
			const cloneMode = submitButton.dataset.cloneMode !== "False";
			submitButton.addEventListener("click", async (event) => {
				event.preventDefault();
				if (cloneMode) {
					const csrfToken = window._galada.csrfToken;
					const response = await cloneUserReport(this.reportId, csrfToken);
					if (response.status === "success") {
						// Redirect to the report page
						window.location.href = response.redirect;
					}
					return;
				}
				this.#clearErrors();
				const data = this.#parseForm();
				const response = await createOrUpdateUserReport(data, this.reportId);
				if (response.status === "success") {
					// Redirect to the report page
					window.location.href = response.redirect;
				}
				this.#parseErrors(response);
			});
		}

		if (this.reportId) {
			// Handle delete button
			const deleteButton = this.element.querySelector("button[data-action='delete']");
			if (deleteButton) {
				deleteButton.addEventListener("click", async (event) => {
					event.preventDefault();
					const data = this.#parseForm();
					const response = await deleteUserReport(this.reportId, data.csrfmiddlewaretoken);
					if (response.status === "success") {
						await this.resolve({ action: "delete" });
						return;
					}
					alert(gettext("Errore durante l'eliminazione del report"));
				});
			}
		}

		// Handle input costraints
		this.#handleInputConstraints();
	}

	#clearErrors() {
		const errors = this.element.querySelectorAll("div.invalid-feedback");
		for (const error of errors) {
			error.innerHTML = "";
		}
		const inputs = this.element.querySelectorAll("input");
		for (const input of inputs) {
			input.ariaInvalid = false;
		}
	}

	#handleInputConstraints() {
		// RPM is aviailable only if website dimension is selected
		const rpmInput = this.element.querySelector("#metric-rpm");
		const websiteDimensionInput = this.element.querySelector("#other-dimensions-website");
		if (rpmInput && websiteDimensionInput) {
			const rmpInputTooltip = interpolate(
				gettext("Seleziona la dimensione <b>%s</b> per abilitare questa metrica"),
				[gettext("Sito")],
			);
			rpmInput.disabled = !websiteDimensionInput.checked;
			if (rpmInput.disabled) {
				this.#activateTooltip(rpmInput.parentNode, rmpInputTooltip);
			} else {
				this.#deactivateTooltip(rpmInput.parentNode);
			}
			websiteDimensionInput.addEventListener("change", (e) => {
				if (e.target.checked) {
					rpmInput.disabled = false;
					this.#deactivateTooltip(rpmInput.parentNode);
				} else {
					rpmInput.disabled = true;
					rpmInput.checked = false;
					this.#activateTooltip(rpmInput.parentNode, rmpInputTooltip);
				}
			});
		}
	}

	#hideDateRangeInputs() {
		// Hide the date range inputs
		this.#dateRangeStartInputEl.parentNode.classList.add("hidden");
		this.#dateRangeEndInputEl.parentNode.classList.add("hidden");
	}

	#activateTooltip(element, content) {
		// If the tooltip is already initialized, update the content
		const instance_ = this.#tooltipsInstances.find((tooltip) => tooltip.referenceEl === element);
		if (instance_) {
			instance_.setContent(content);
			instance_.activate();
			return;
		}
		// Create a new tooltip instance
		const instance = new Popover(element, {
			class: "text-sm",
			placement: "bottom",
			trigger: "hover",
		});
		instance.setContent(content);
		this.#tooltipsInstances.push(instance);
	}

	#deactivateTooltip(element) {
		const instance = this.#tooltipsInstances.find((tooltip) => tooltip.referenceEl === element);
		if (instance) {
			instance.deactivate();
		}
	}

	#parseErrors(data) {
		if (data.errors !== undefined) {
			data.errors.forEach((error, index) => {
				const input = this.element.querySelector(`input[name=${error.field}]`);
				if (input !== null) {
					input.parentNode.querySelector("div.invalid-feedback").innerHTML = error.message;
					input.ariaInvalid = true;
				}
				// Scroll to first error input
				if (index === 0) {
					input.scrollIntoView();
				}
			});
		}
	}

	#parseForm() {
		const formData = new FormData(this.#form);
		const data = {};

		for (const [key, value] of formData) {
			// Handle array inputs
			if (key.includes("[]")) {
				const key_ = key.replace("[]", "");
				if (data[key_] === undefined) {
					data[key_] = [];
				}
				data[key_].push(value);
				continue;
			}
			data[key] = value;
		}

		// Merge filter builder data (overrides any form fields with same name)
		if (this.#filtersBuilder) {
			Object.assign(data, this.#filtersBuilder.serialize());
		}

		return data;
	}

	#validateDateInput(inputEl) {
		const feedback = inputEl.parentNode.querySelector("div.invalid-feedback");
		if (!feedback) return;

		if (inputEl.validity.badInput) {
			feedback.innerHTML = gettext(
				"Data non valida — il giorno inserito non esiste per questo mese.",
			);
			inputEl.ariaInvalid = "true";
		} else {
			feedback.innerHTML = "";
			inputEl.ariaInvalid = "false";
		}

		const submitButton = this.element.querySelector("button[type=submit]");
		if (submitButton) {
			const hasInvalidDate =
				this.#dateRangeStartInputEl.validity.badInput ||
				this.#dateRangeEndInputEl.validity.badInput;
			submitButton.disabled = hasInvalidDate;
		}
	}

	#showDateRangeInputs() {
		if (!this.#dateRangeStartInputEl.value) {
			this.#dateRangeStartInputEl.value = this.#yesterday.format("YYYY-MM-DD");
		}
		if (!this.#dateRangeEndInputEl.value) {
			this.#dateRangeEndInputEl.value = this.#yesterday.format("YYYY-MM-DD");
		}
		for (const input of [this.#dateRangeStartInputEl, this.#dateRangeEndInputEl]) {
			input.addEventListener("change", () => this.#validateDateInput(input));
		}
		this.#dateRangeStartInputEl.parentNode.classList.remove("hidden");
		this.#dateRangeEndInputEl.parentNode.classList.remove("hidden");
	}

	#getCommonTomSelectConfig() {
		return {
			plugins: {
				clear_button: {
					html: (data) => {
						return `<div class="${data.className}" title="${data.title}">${base64ToSvg(xSvg).outerHTML}</div>`;
					},
					title: gettext("Rimuovi tutto"),
				},
				remove_button: {},
			},
			render: {
				item: (data, escape_) => this.#renderItem(data, escape_),
				option: (data, escape_) => this.#renderOption(data, escape_),
				optgroup_header: (data, escape_) => this.#renderOptgroupHeader(data, escape_),
				no_results: () => `<div class="no-results">${gettext("Nessun risultato trovato")}</div>`,
				loading: () => `
					<div class="flex items-center justify-center p-4">
						<div class="animate-spin">
							${base64ToSvg(loaderCircleSvg).outerHTML}
						</div>
					</div>`,
			},
			onClear: function () {
				this.refreshOptions();
			},
		};
	}

	#renderItem(data, escape_) {
		if (data.favicon) {
			const faviconUrl = new URL(data.favicon, window.location.origin);
			return `
				<div class="inline-flex items-center gap-1">
					<img src="${faviconUrl.href}" class="rounded-full bg-slate-950/50" width="16" height="16"/>${escape_(data.text)}
				</div>`;
		}
		return `<div class="item">${escape_(data.text)}</div>`;
	}

	#renderOption(data, escape_) {
		if (data.favicon) {
			const faviconUrl = new URL(data.favicon, window.location.origin);
			return `
				<div class="flex items-center gap-1">
					<img src="${faviconUrl.href}" class="rounded-full bg-slate-950/50" width="16" height="16" loading="lazy"/>${escape_(data.text)}
				</div>`;
		}
		return `<div class="option">${escape_(data.text)}</div>`;
	}

	#renderOptgroupHeader(data, escape_) {
		const div = document.createElement("div");
		div.className =
			"flex text-xs font-bold items-center justify-between p-2 bg-sky-800/10 dark:bg-white/5";

		const span = document.createElement("span");
		span.className = "text-black dark:text-white";
		span.textContent = escape_(data.label);

		div.append(span);
		return div;
	}
}
